import { useState, useEffect, useCallback } from 'react';
import { Clock, Check, X, Phone, User, Scissors, Calendar, Loader2, AlertCircle, RefreshCw, Shield } from 'lucide-react';
import { bookingSecurityApi } from '../../api/bookingSecurityApi';
import type { PendingBooking } from '../../api/bookingSecurityApi';
import { useSalonId } from '../../hooks/useSalonId';
import './BookingApprovalPanel.css';

const BookingApprovalPanel = () => {
    const salonId = useSalonId();
    const [bookings, setBookings] = useState<PendingBooking[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [rejectingId, setRejectingId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');

    const fetchPending = useCallback(async () => {
        if (!salonId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await bookingSecurityApi.getPendingBookings(salonId);
            setBookings(data);
        } catch (err) {
            console.error('Error fetching pending bookings:', err);
            setError('Error al cargar las reservas pendientes');
        } finally {
            setLoading(false);
        }
    }, [salonId]);

    useEffect(() => {
        fetchPending();
    }, [fetchPending]);

    const handleApprove = async (bookingId: string) => {
        try {
            setProcessingId(bookingId);
            await bookingSecurityApi.makeDecision(bookingId, {
                action: 'APPROVE',
            });
            setBookings(prev => prev.filter(b => b.id !== bookingId));
        } catch (err) {
            console.error('Error approving booking:', err);
            setError('Error al aprobar la reserva');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (bookingId: string) => {
        try {
            setProcessingId(bookingId);
            await bookingSecurityApi.makeDecision(bookingId, {
                action: 'REJECT',
                rejectReason: rejectReason || undefined,
            });
            setBookings(prev => prev.filter(b => b.id !== bookingId));
            setRejectingId(null);
            setRejectReason('');
        } catch (err) {
            console.error('Error rejecting booking:', err);
            setError('Error al rechazar la reserva');
        } finally {
            setProcessingId(null);
        }
    };

    const formatDateTime = (iso: string) => {
        const date = new Date(iso);
        return {
            date: date.toLocaleDateString('es-CO', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
            }),
            time: date.toLocaleTimeString('es-CO', {
                hour: '2-digit',
                minute: '2-digit',
            }),
        };
    };

    const getTimeSince = (iso: string) => {
        const diff = Date.now() - new Date(iso).getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 60) return `hace ${minutes}min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `hace ${hours}h`;
        const days = Math.floor(hours / 24);
        return `hace ${days}d`;
    };

    if (loading) {
        return (
            <div className="approval-panel">
                <div className="approval-loading">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                    <p>Cargando reservas pendientes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="approval-panel">
            {/* Header */}
            <div className="approval-header">
                <div className="approval-header-left">
                    <div className="approval-icon-wrapper">
                        <Shield className="approval-icon" />
                    </div>
                    <div>
                        <h2>Aprobación de Reservas</h2>
                        <p>{bookings.length} pendiente{bookings.length !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <button className="btn-refresh" onClick={fetchPending} title="Actualizar">
                    <RefreshCw className="w-4 h-4" />
                </button>
            </div>

            {error && (
                <div className="approval-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}

            {bookings.length === 0 ? (
                <div className="approval-empty">
                    <div className="empty-icon">✨</div>
                    <h3>¡Todo al día!</h3>
                    <p>No tienes reservas pendientes de aprobación.</p>
                </div>
            ) : (
                <div className="approval-list">
                    {bookings.map(booking => {
                        const { date, time } = formatDateTime(booking.startTime);
                        const isProcessing = processingId === booking.id;
                        const isRejecting = rejectingId === booking.id;

                        return (
                            <div key={booking.id} className={`approval-card ${isProcessing ? 'processing' : ''}`}>
                                <div className="card-top">
                                    <div className="card-time-badge">
                                        <Clock className="w-3 h-3" />
                                        {getTimeSince(booking.createdAt)}
                                    </div>
                                    {booking.phoneVerified && (
                                        <div className="card-verified-badge">
                                            <Shield className="w-3 h-3" />
                                            Verificado
                                        </div>
                                    )}
                                </div>

                                <div className="card-body">
                                    <div className="card-info-row">
                                        <User className="card-info-icon" />
                                        <span className="card-info-name">{booking.clientName}</span>
                                    </div>
                                    <div className="card-info-row">
                                        <Phone className="card-info-icon" />
                                        <span>{booking.clientPhone}</span>
                                    </div>
                                    <div className="card-info-row">
                                        <Calendar className="card-info-icon" />
                                        <span>{date} a las {time}</span>
                                    </div>
                                    <div className="card-info-row">
                                        <Scissors className="card-info-icon" />
                                        <span>Servicio #{booking.serviceId.slice(0, 8)}</span>
                                    </div>
                                </div>

                                {isRejecting ? (
                                    <div className="reject-form">
                                        <textarea
                                            value={rejectReason}
                                            onChange={(e) => setRejectReason(e.target.value)}
                                            placeholder="Motivo del rechazo (opcional)..."
                                            rows={2}
                                            className="reject-textarea"
                                        />
                                        <div className="reject-form-actions">
                                            <button
                                                className="btn-cancel-reject"
                                                onClick={() => { setRejectingId(null); setRejectReason(''); }}
                                                disabled={isProcessing}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                className="btn-confirm-reject"
                                                onClick={() => handleReject(booking.id)}
                                                disabled={isProcessing}
                                            >
                                                {isProcessing ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <X className="w-4 h-4" /> Rechazar
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="card-actions">
                                        <button
                                            className="btn-reject"
                                            onClick={() => setRejectingId(booking.id)}
                                            disabled={isProcessing}
                                        >
                                            <X className="w-4 h-4" /> Rechazar
                                        </button>
                                        <button
                                            className="btn-approve"
                                            onClick={() => handleApprove(booking.id)}
                                            disabled={isProcessing}
                                        >
                                            {isProcessing ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Check className="w-4 h-4" /> Aprobar
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default BookingApprovalPanel;
